import { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Activity, ArrowLeft, BarChart3, BookOpen, Bot, Gauge, Hash, HeartPulse, KeyRound, Mail, ShieldCheck, Users, Webhook as WebhookIcon } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { IconRail } from "@/components/IconRail";
import { MobileTabBar } from "@/components/MobileTabBar";
import { HealthPill } from "@/components/HealthPill";
import { Modals } from "@/components/Modals";
import { useChat } from "@/lib/chat";
import { analytics } from "@/lib/analytics";
import { useIsMobile } from "@/lib/device";
import { Overview } from "./admin/Overview";
import { EventsLog } from "./admin/EventsLog";
import { UsersReport, ChannelsReport } from "./admin/Reports";
import { ApiKeys, ApiDocs } from "./admin/ApiKeys";
import { Webhooks } from "./admin/Webhooks";
import { EmailDelivery } from "./admin/EmailDelivery";
import { Performance } from "./admin/Performance";
import { Health } from "./admin/Health";
import { PermissionsMatrix } from "./admin/Permissions";
import { Bots } from "./admin/Bots";

// [id, label, icon, required permission ("admin" = admins only)]
const NAV = [
  ["overview", "Analytics overview", BarChart3, "admin.reports"],
  ["health", "Health center", HeartPulse, "admin.health"],
  ["performance", "Team performance", Gauge, "admin.reports"],
  ["events", "Activity log", Activity, "admin.reports"],
  ["users", "Members", Users, "admin.console"],
  ["permissions", "Permissions", ShieldCheck, "admin"],
  ["channels", "Channels", Hash, "admin.reports"],
  ["bots", "Bots", Bot, "admin.integrations"],
  ["email", "Email delivery", Mail, "admin.integrations"],
  ["api-keys", "API keys", KeyRound, "admin.integrations"],
  ["webhooks", "Webhooks", WebhookIcon, "admin.integrations"],
  ["api-docs", "API docs", BookOpen, "admin.integrations"],
];

export default function AdminPage() {
  const { section = "overview" } = useParams();
  const navigate = useNavigate();
  const { me, can } = useChat();
  const isMobile = useIsMobile();
  useEffect(() => { analytics.setContext({ conversation_id: null }); analytics.track("page_view", { category: "navigation", label: `/admin/${section}`, target: "admin" }); }, [section]);
  if (!me) return null;
  const allowed = (perm) => (perm === "admin" ? !!me.is_admin : can(perm));
  const nav = NAV.filter(([, , , perm]) => allowed(perm));
  if (!can("admin.console") || !nav.length) return <Navigate to="/client" replace />;
  const entry = NAV.find(([id]) => id === section);
  if (!entry || !allowed(entry[3])) return <Navigate to={`/admin/${nav[0][0]}`} replace />;
  const Body = { overview: Overview, health: Health, performance: Performance, events: EventsLog, users: UsersReport, permissions: PermissionsMatrix, bots: Bots, channels: ChannelsReport, email: EmailDelivery, "api-keys": ApiKeys, webhooks: Webhooks, "api-docs": ApiDocs }[section] || Overview;
  return (
    <div className={`tc-app${isMobile ? " is-mobile" : ""}`} data-testid="admin-page" data-layout={isMobile ? "mobile" : "desktop"}>
      <TopBar />
      <div className="tc-body">
        <IconRail view="admin" />
        <div className="tc-admin">
          <nav className="tc-admin__nav" data-testid="admin-nav">
            <div className="tc-admin__navtitle">Admin</div>
            {isMobile && <button className="tc-admin__navitem" onClick={() => navigate("/client/home")} data-testid="admin-nav-back"><ArrowLeft /> Chat</button>}
            {isMobile && <HealthPill />}
            {nav.map(([id, label, Icon]) => <button key={id} className={`tc-admin__navitem${section === id ? " is-active" : ""}`} onClick={() => navigate(`/admin/${id}`)} data-testid={`admin-nav-${id}`}><Icon /> {label}</button>)}
          </nav>
          <div className="tc-admin__content tc-scroll"><Body /></div>
        </div>
      </div>
      {isMobile && <MobileTabBar view="admin" />}
      <Modals />
    </div>
  );
}
